import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiDollarSign, FiTrendingUp, FiBarChart } = FiIcons;

const PriceIntelligence = ({ selectedProduct }) => {
  // Mock NSP and WAC pricing data
  const pricingData = [
    {
      manufacturer: 'AMNEAL PHARMA',
      pack: 'Tab FC 250MG 60',
      nsp: 147.19,
      wac: null,
      color: '#3b82f6'
    },
    {
      manufacturer: 'SUN PHARMACEUTICAL',
      pack: 'Tab 250MG 120',
      nsp: 306.59,
      wac: null,
      color: '#10b981'
    },
    {
      manufacturer: 'APOTEX CORP',
      pack: 'Tab FC 250MG 60',
      nsp: 173.15,
      wac: null,
      color: '#f59e0b'
    },
    {
      manufacturer: 'NOVAGEN PHARMA',
      pack: 'Tab FC 250MG 120',
      nsp: 314.68,
      wac: 335.89,
      color: '#8b5cf6'
    },
    {
      manufacturer: 'VIATRIS',
      pack: 'Tab FC 500MG 60',
      nsp: 272.81,
      wac: 309.26,
      color: '#ef4444'
    },
    {
      manufacturer: 'DR REDDY\'S LAB',
      pack: 'Tab FC 250MG 120',
      nsp: 314.50,
      wac: 347.39,
      color: '#06b6d4'
    },
    {
      manufacturer: 'NORTHSTAR RX',
      pack: 'Tab FC 250MG 120',
      nsp: 314.50,
      wac: 346.95,
      color: '#84cc16'
    },
    {
      manufacturer: 'FEDERAL PHARMA INDI',
      pack: 'Tab FC 500MG 60',
      nsp: 272.81,
      wac: 305.69,
      color: '#f97316'
    },
    {
      manufacturer: 'GLENMARK PHARMA',
      pack: 'Tab FC 250MG 120',
      nsp: 314.50,
      wac: 332.70,
      color: '#ec4899'
    },
    {
      manufacturer: 'NOVADOZ PHARMA',
      pack: 'Tab FC 250MG 120',
      nsp: 285.20,
      wac: 312.45,
      color: '#1e40af',
      isOurs: true
    }
  ];

  // Mock MAT pricing trends
  const matPricing = [
    { period: 'MAT May 2023', avgNSP: 289.45, avgWAC: 318.20 },
    { period: 'MAT May 2024', avgNSP: 294.15, avgWAC: 324.80 },
    { period: 'MAT May 2025', avgNSP: 298.75, avgWAC: 331.15 }
  ];

  const formatCurrency = (amount) => {
    if (!amount) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const getCompetitivePosition = (ourPrice, avgPrice) => {
    const difference = ((ourPrice - avgPrice) / avgPrice) * 100;
    if (difference < -5) return { text: 'Competitive Advantage', color: 'text-green-600', bg: 'bg-green-100' };
    if (difference > 5) return { text: 'Premium Pricing', color: 'text-red-600', bg: 'bg-red-100' };
    return { text: 'Market Aligned', color: 'text-blue-600', bg: 'bg-blue-100' };
  };

  const avgNSP = pricingData.reduce((sum, item) => sum + item.nsp, 0) / pricingData.length;
  const ourNSP = pricingData.find(item => item.isOurs)?.nsp || 0;
  const position = getCompetitivePosition(ourNSP, avgNSP);

  return (
    <div className="space-y-6">
      {/* Price Intelligence Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6"
      >
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <SafeIcon icon={FiDollarSign} className="text-green-600" />
          NSP and WAC Price Intelligence - {selectedProduct}
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="bg-blue-50 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <SafeIcon icon={FiBarChart} className="text-blue-600" />
              <h4 className="font-medium text-blue-900">Average NSP</h4>
            </div>
            <p className="text-2xl font-bold text-blue-900">{formatCurrency(avgNSP)}</p>
            <p className="text-sm text-blue-700">Market average</p>
          </div>

          <div className="bg-purple-50 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <SafeIcon icon={FiTrendingUp} className="text-purple-600" />
              <h4 className="font-medium text-purple-900">Our NSP</h4>
            </div>
            <p className="text-2xl font-bold text-purple-900">{formatCurrency(ourNSP)}</p>
            <p className="text-sm text-purple-700">Novadoz pricing</p>
          </div>

          <div className="bg-orange-50 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <SafeIcon icon={FiDollarSign} className="text-orange-600" />
              <h4 className="font-medium text-orange-900">Price Variance</h4>
            </div>
            <p className="text-2xl font-bold text-orange-900">
              {((ourNSP - avgNSP) / avgNSP * 100).toFixed(1)}%
            </p>
            <p className="text-sm text-orange-700">vs market avg</p>
          </div>

          <div className="bg-green-50 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <SafeIcon icon={FiTrendingUp} className="text-green-600" />
              <h4 className="font-medium text-green-900">Position</h4>
            </div>
            <p className={`text-sm font-medium px-2 py-1 rounded ${position.bg} ${position.color}`}>
              {position.text}
            </p>
          </div>
        </div>
      </motion.div>

      {/* Pricing Comparison Chart */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6"
      >
        <h4 className="text-lg font-semibold text-gray-900 mb-6">NSP Price Comparison by Manufacturer</h4>
        
        <div className="space-y-4">
          {pricingData
            .sort((a, b) => b.nsp - a.nsp)
            .map((item, index) => (
            <div key={index} className={`flex items-center gap-4 p-4 rounded-lg ${item.isOurs ? 'bg-blue-50 border border-blue-200' : 'bg-gray-50'}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-2">
                  <h5 className={`font-medium truncate ${item.isOurs ? 'text-blue-900' : 'text-gray-900'}`}>
                    {item.manufacturer}
                    {item.isOurs && <span className="ml-2 text-xs bg-blue-600 text-white px-2 py-1 rounded">OUR PRICE</span>}
                  </h5>
                  <span className={`font-bold ${item.isOurs ? 'text-blue-900' : 'text-gray-900'}`}>
                    {formatCurrency(item.nsp)}
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">{item.pack}</span>
                  {item.wac && (
                    <span className="text-gray-600">WAC: {formatCurrency(item.wac)}</span>
                  )}
                </div>
                <div className="mt-2">
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${(item.nsp / Math.max(...pricingData.map(d => d.nsp))) * 100}%` }}
                      transition={{ delay: index * 0.1, duration: 0.5 }}
                      className="h-2 rounded-full"
                      style={{ backgroundColor: item.color }}
                    ></motion.div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </motion.div>

      {/* MAT Pricing Trends */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden"
      >
        <div className="p-6 border-b border-gray-200">
          <h4 className="text-lg font-semibold text-gray-900">MAT Pricing Trends</h4>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="text-left py-4 px-6 font-semibold text-gray-900">Period</th>
                <th className="text-right py-4 px-6 font-semibold text-gray-900">Average NSP</th>
                <th className="text-right py-4 px-6 font-semibold text-gray-900">Average WAC</th>
                <th className="text-right py-4 px-6 font-semibold text-gray-900">NSP Growth</th>
                <th className="text-right py-4 px-6 font-semibold text-gray-900">WAC Growth</th>
              </tr>
            </thead>
            <tbody>
              {matPricing.map((period, index) => (
                <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="py-4 px-6 font-medium text-gray-900">{period.period}</td>
                  <td className="py-4 px-6 text-right font-medium">{formatCurrency(period.avgNSP)}</td>
                  <td className="py-4 px-6 text-right font-medium">{formatCurrency(period.avgWAC)}</td>
                  <td className="py-4 px-6 text-right">
                    {index > 0 && (
                      <span className={`font-medium ${
                        period.avgNSP > matPricing[index - 1].avgNSP ? 'text-red-600' : 'text-green-600'
                      }`}>
                        {((period.avgNSP - matPricing[index - 1].avgNSP) / matPricing[index - 1].avgNSP * 100).toFixed(1)}%
                      </span>
                    )}
                  </td>
                  <td className="py-4 px-6 text-right">
                    {index > 0 && (
                      <span className={`font-medium ${
                        period.avgWAC > matPricing[index - 1].avgWAC ? 'text-red-600' : 'text-green-600'
                      }`}>
                        {((period.avgWAC - matPricing[index - 1].avgWAC) / matPricing[index - 1].avgWAC * 100).toFixed(1)}%
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </motion.div>
    </div>
  );
};

export default PriceIntelligence;